import type { SiteThemeConfig } from "@site-factory/config";

/**
 * Default neutral theme - clean, professional baseline
 */
const defaultTheme: SiteThemeConfig = {
  // Core colors
  primary: "#2563eb",
  secondary: "#475569",
  accent: "#f59e0b",
  background: "#ffffff",
  text: "#1e293b",

  // Extended colors
  muted: "#64748b",
  border: "#e2e8f0",
  primaryForeground: "#ffffff",
  secondaryForeground: "#ffffff",
  accentForeground: "#1e293b",
  danger: "#ef4444",
  dangerForeground: "#ffffff",
  ring: "#2563eb",

  // Surfaces
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  surfaceElevated: "#f1f5f9",
  surface2: "#f1f5f9",

  // Shape tokens
  radiusSm: "0.375rem",
  radiusMd: "0.5rem",
  radiusLg: "0.75rem",
  cardRadius: "0.75rem",
  buttonRadius: "0.5rem",

  // Elevation tokens
  shadowSm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  shadowMd: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
  shadowLg: "0 10px 15px -3px rgb(0 0 0 / 0.1)",
  cardShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",

  // Dark mode
  dark: {
    primary: "#60a5fa",
    secondary: "#94a3b8",
    accent: "#fbbf24",
    background: "#0f172a",
    text: "#e2e8f0",
    muted: "#94a3b8",
    border: "#334155",
    surface: "#1e293b",
    surfaceAlt: "#334155",
    surfaceElevated: "#1e293b",
    surface2: "#334155",
    cardShadow: "0 10px 15px -3px rgb(0 0 0 / 0.4)",
  },
};

/**
 * Polar Pop theme - Sno Biz brand colors
 * Exact vibrant colors extracted from sticker-bizzy logo
 * Brand colors: Cyan, Pink, Orange, Yellow, Purple
 */
const polarPopTheme: SiteThemeConfig = {
  // Core colors - EXACT colors from sticker-bizzy logo
  primary: "#4EACE2",      // Bright cyan (from guitar/ice cup in logo)
  secondary: "#E91E8C",    // Hot pink/magenta (from sunglasses in logo)
  accent: "#FF8C00",       // Bright orange (from beak in logo)
  background: "#ffffff",   // Clean white background
  text: "#1a1a1a",         // Dark text for readability

  // Extended colors - incorporating all logo colors
  muted: "#6B7280",              // Neutral gray for secondary text
  border: "#E5E7EB",             // Light border
  primaryForeground: "#ffffff",  // White text on cyan
  secondaryForeground: "#ffffff", // White text on pink
  accentForeground: "#ffffff",   // White text on orange
  danger: "#EF4444",             // Standard red for errors
  dangerForeground: "#ffffff",
  ring: "#E91E8C",               // Pink focus rings (fun!)

  // Surfaces - use subtle brand color tints for visual interest
  surface: "#ffffff",            // White cards
  surfaceAlt: "#F0FBFF",         // Very light cyan tint
  surfaceElevated: "#ffffff",    // Elevated cards stay white
  surface2: "#FFF9F0",           // Very light peachy tint

  // Shape tokens - playful, rounded
  radiusSm: "0.5rem",
  radiusMd: "0.75rem",
  radiusLg: "1rem",
  cardRadius: "1rem",
  buttonRadius: "2rem",          // Full rounded buttons for fun!

  // Elevation tokens - colorful shadows with cyan tint
  shadowSm: "0 2px 8px rgba(78, 172, 226, 0.12)",
  shadowMd: "0 4px 16px rgba(78, 172, 226, 0.18)",
  shadowLg: "0 8px 24px rgba(78, 172, 226, 0.24)",
  cardShadow: "0 4px 16px rgba(78, 172, 226, 0.15)",

  // Gradients - vibrant multi-color using EXACT logo colors (cyan, pink, orange, yellow)
  gradientHeroStrong: "linear-gradient(135deg, #4EACE2 0%, #E91E8C 33%, #FF8C00 66%, #FFC700 100%)",
  gradientHeroSoft: "linear-gradient(135deg, rgba(78, 172, 226, 0.15) 0%, rgba(233, 30, 140, 0.12) 33%, rgba(255, 140, 0, 0.10) 66%, rgba(255, 199, 0, 0.12) 100%)",
  gradientSurfaceBand:
    "radial-gradient(circle at 15% 20%, rgba(78, 172, 226, 0.06), transparent 50%), radial-gradient(circle at 85% 30%, rgba(233, 30, 140, 0.05), transparent 50%), radial-gradient(circle at 50% 80%, rgba(255, 140, 0, 0.05), transparent 50%), radial-gradient(circle at 70% 60%, rgba(255, 199, 0, 0.04), transparent 50%), #ffffff",

  // Dark mode - maintain all vibrant colors
  dark: {
    primary: "#4EACE2",        // Keep cyan vibrant in dark mode
    secondary: "#E91E8C",      // Keep pink vibrant
    accent: "#FF8C00",         // Keep orange vibrant
    background: "#0F172A",     // Dark navy background
    text: "#F1F5F9",           // Light text
    muted: "#94A3B8",
    border: "#334155",
    surface: "#1E293B",
    surfaceAlt: "#334155",
    surfaceElevated: "#1E293B",
    surface2: "#334155",
    cardShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
  },
};

/**
 * Central theme registry - single source of truth for all themes
 */
export const themeRegistry = {
  default: defaultTheme,
  "polar-pop": polarPopTheme,
} as const;

export type ThemeName = keyof typeof themeRegistry;

/**
 * Get a theme by name from the registry
 */
export function getTheme(name: ThemeName): SiteThemeConfig {
  return themeRegistry[name];
}
